import { MoreVertical, Pin, Send } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { GestureResponderEvent, TextInput } from "react-native";
import { View, XStack, YStack } from "tamagui";
import { useApi } from "../../../core/apis";
import { Permission } from "../../../permissions/types";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Group } from "../../../ui/Group";
import { log } from "../../../ui/log";
import { Pill, PillType } from "../../../ui/Pill";
import { Subtext } from "../../../ui/Subtext";
import { FeedItemCommentUI } from "../../feedcomment/FeedItemCommentUI";
import {
  BusinessFeedEventType,
  type BusinessFeedItem,
} from "../BusinessFeedItem";
import { FeedItemBusinessModuleStatusUI } from "../events/FeedItemBusinessModuleStatus";
import { FeedItemShoutoutUI } from "../events/FeedItemShoutout";
import { FeedItemTextUI } from "../events/FeedItemText";
import { FeedItemUserIntroUI } from "../events/FeedItemUserIntro";
import { FeedItemManageSheet } from "./FeedItemManageSheet";
import { FeedItemReactionShareSheet } from "./FeedItemReactionsShareSheet";
import { FeedTextInput } from "./FeedTextInput";

export const FeedItemUI = observer(({ item }: { item: BusinessFeedItem }) => {
  let content = null;

  const { businessFeedCommentsApi } = useApi();
  const { t } = useTranslation();
  const commentInputRef = React.useRef<TextInput>(null);
  const [showReactionSheet, setShowReactionSheet] = React.useState(false);
  const [showManageSheet, setShowManageSheet] = React.useState(false);

  const [isEditMode, setIsEditMode] = React.useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);

  let editable = true;

  if (item.data.hidden) {
    content = (
      <YStack
        paddingVertical={"$largest"}
        paddingHorizontal={"$space.larger"}
        gap={"$medium"}
      >
        <Body muted fontStyle={"italic"}>
          {t("business:feed_content_hidden")}
        </Body>
      </YStack>
    );
  } else if (item.data.type === BusinessFeedEventType.GroupShoutOut) {
    content = (
      <FeedItemShoutoutUI
        item={item}
        isEditMode={isEditMode}
        onEdit={() => setIsEditMode(false)}
      />
    );
  } else if (item.data.type === BusinessFeedEventType.Text) {
    content = (
      <FeedItemTextUI
        item={item}
        isEditMode={isEditMode}
        onEdit={() => setIsEditMode(false)}
      />
    );
  } else if (item.data.type === BusinessFeedEventType.UserCompletedIntro) {
    content = <FeedItemUserIntroUI item={item} />;
    editable = false;
  } else if (item.data.type === BusinessFeedEventType.BusinessModuleStatus) {
    content = <FeedItemBusinessModuleStatusUI item={item} />;
    editable = false;
  } else {
    log.dev(`Unhandled feedItem event ${item.data.type}`);
  }

  if (!content) {
    return null;
  }

  if (item.data.deleted) {
    return (
      <Box>
        <Body muted fontStyle={"italic"}>
          {t("business:feed_content_deleted")}
        </Body>
      </Box>
    );
  }

  const comments = item.comments;
  const isShoutout = item.data.type === BusinessFeedEventType.GroupShoutOut;

  return (
    <View>
      <Box noPadding gap={0}>
        <View
          borderLeftWidth={isShoutout ? 5 : 0}
          borderLeftColor={"$pressablePrimaryBackground"}
        >
          <View
            position={"absolute"}
            right={"$small"}
            top={"$medium"}
            padding={"$medium"}
            zIndex={2}
            onPress={(e: GestureResponderEvent) => {
              e.stopPropagation();
              setShowManageSheet(!showManageSheet);
            }}
            cursor={"pointer"}
          >
            <MoreVertical color={"$muted"} />
          </View>

          {item.data.pinned && (
            <View zIndex={10} position={"absolute"} top={20} right={50}>
              <Pin color="$muted" size={40} rotate={"45deg"} />
            </View>
          )}

          {!!item.data.reported &&
            item.businessPlan.checkPermission(
              Permission.CanManageFeedItems,
            ) && (
              <Body
                muted
                fontStyle={"italic"}
                paddingHorizontal={"$space.larger"}
                paddingTop={"$medium"}
              >
                {t("business:feed_content_reported")}
              </Body>
            )}

          {content}
        </View>

        <XStack
          paddingVertical={"$medium"}
          gap={"$medium"}
          paddingHorizontal={"$space.larger"}
          alignItems="center"
          width={"100%"}
          cursor={"pointer"}
          borderTopColor={"$divider"}
          borderTopWidth={1}
        >
          {!showCommentInput && (
            <Subtext
              textDecorationLine="underline"
              cursor="pointer"
              onPress={() => {
                setShowCommentInput(true);
                setTimeout(() => {
                  commentInputRef.current?.focus();
                }, 20);
              }}
            >
              {t("business:feed_comment")}
            </Subtext>
          )}

          <Pill
            type={PillType.Muted}
            onPress={() => {
              setShowReactionSheet(true);
            }}
          >
            <XStack justifyContent={"center"} gap={"$small"}>
              <Subtext lineHeight={16}>+ {t("business:feed_like")}</Subtext>
            </XStack>
          </Pill>
          {item.hasReactions &&
            item.reactionRowItemCounts.map((reaction, index) => {
              const Icon = reaction.icon;

              return (
                <Pill
                  key={index}
                  type={PillType.Muted}
                  onPress={() => {
                    setShowReactionSheet(true);
                  }}
                >
                  <XStack justifyContent={"center"} gap={"$small"}>
                    <Icon size={16} /> <Subtext>{reaction.count}</Subtext>
                  </XStack>
                </Pill>
              );
            })}
        </XStack>
      </Box>

      {comments.length ? (
        <View
          paddingHorizontal={"$larger"}
          paddingVertical={"$medium"}
          backgroundColor={"$backgroundMutedBlock"}
          borderTopColor={"$divider"}
          borderTopWidth={1}
        >
          <Group borderTop={false}>
            {item.hasMoreComments && (
              <Body
                width="100%"
                textAlign="center"
                muted
                textDecorationLine="underline"
                cursor="pointer"
                onPress={() => {
                  void businessFeedCommentsApi.getFeedCommentsForFeedItem(
                    item.uid,
                    comments[0].data.ts,
                  );
                }}
              >
                {t("business:feed_load_comments")}
              </Body>
            )}

            {comments.map((comment) => (
              <FeedItemCommentUI key={comment.uid} comment={comment} />
            ))}
          </Group>
        </View>
      ) : undefined}

      {showCommentInput && (
        <FeedTextInput
          businessPlan={item.businessPlan}
          ref={commentInputRef}
          borderTopWidth={1}
          borderTopColor={"$divider"}
          type="padded"
          placeholder={t("business:feed_add_comment")}
          icon={Send}
          submitOnEnter
          onSubmitEditing={(e) => {
            item.addComment({
              text: e.nativeEvent.text,
            });
          }}
        />
      )}

      <FeedItemReactionShareSheet
        sheetId={"feed_reactions"}
        feedItemUid={item.uid}
        open={showReactionSheet}
        onOpenChange={setShowReactionSheet}
      />

      <FeedItemManageSheet
        sheetId={"feed_manage"}
        likeOption={{
          show: !item.isAuthor,
          callback: () => {
            setShowManageSheet(false);
            setShowReactionSheet(true);
          },
        }}
        editOption={{
          show: editable && item.isAuthor,
          callback: () => setIsEditMode(!isEditMode),
        }}
        pinOption={{
          show: item.isAdmin,
          isPinned: item.isPinned,
          callback: async () => {
            await item.saveEditAction("pinned");
            await item.businessPlan.toggleFeedItemIsPinned(item);
          },
        }}
        hideOption={{
          show: item.isAdmin,
          isHidden: item.data.hidden,
          callback: () => item.saveEditAction("hidden"),
        }}
        deleteOption={{
          show: item.isAdmin || item.isAuthor,
          callback: () => item.saveEditAction("deleted"),
        }}
        reportOption={{
          show: !item.isAuthor,
          callback: () => item.saveEditAction("reported"),
        }}
        open={showManageSheet}
        onOpenChange={setShowManageSheet}
      />
    </View>
  );
});
